// =====================================================================================
// INVRT Radar — web console — exact decimal arithmetic
//
// The console does almost no arithmetic on money, by design: every authoritative figure
// (a quote's credits, a balance, a window total) is computed in Postgres and displayed
// verbatim. There is exactly ONE exception, and it lives here — the "balance after this
// reveal" projection on the approve card, which has to exist because a person cannot
// consent to a spend without seeing where it leaves them.
//
// It is done with BigInt on the decimal digits, never with floating point. A reveal costs
// figures like 0.5 and 2 credits and a visitor company burn is 0.25; `0.1 + 0.2 !== 0.3`
// in binary floating point, and a projection that disagreed with the ledger by a rounding
// error would undermine the one number the customer is being asked to trust.
//
// The projection is LABELLED as a projection in the UI. The authoritative figure arrives
// in the reveal response's own `balance_after`, straight out of Postgres, and replaces it.
// =====================================================================================

import type { Numeric } from "./types";

const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/;

interface ParsedDecimal {
  /** All digits as an integer, sign applied. */
  scaled: bigint;
  /** How many digits sit after the point. */
  scale: number;
}

function parse(value: Numeric): ParsedDecimal | null {
  const text = (typeof value === "number" ? String(value) : value).trim();
  const match = DECIMAL_PATTERN.exec(text);
  if (!match) return null;

  const [, sign, whole = "0", fraction = ""] = match;
  const digits = BigInt(`${whole}${fraction}`);
  return { scaled: sign === "-" ? -digits : digits, scale: fraction.length };
}

function render(scaled: bigint, scale: number): string {
  const negative = scaled < 0n;
  const digits = (negative ? -scaled : scaled).toString().padStart(scale + 1, "0");
  const cut = digits.length - scale;
  const text = scale === 0 ? digits : `${digits.slice(0, cut)}.${digits.slice(cut)}`;
  return `${negative ? "-" : ""}${text}`;
}

/**
 * `a - b`, exactly, as a decimal string. Returns null when either side is not a plain
 * decimal — a caller that gets null must show no projection rather than a wrong one.
 */
export function subtractExact(a: Numeric, b: Numeric): string | null {
  const left = parse(a);
  const right = parse(b);
  if (!left || !right) return null;

  const scale = Math.max(left.scale, right.scale);
  const lift = (parsed: ParsedDecimal): bigint =>
    parsed.scaled * 10n ** BigInt(scale - parsed.scale);

  return render(lift(left) - lift(right), scale);
}

/** True when the value parses as a plain decimal, so a comparison is meaningful. */
export function isDecimal(value: Numeric | null | undefined): boolean {
  return value !== null && value !== undefined && parse(value) !== null;
}

/** `a < b`. Returns null when either side is not a plain decimal. */
export function isLessThan(a: Numeric, b: Numeric): boolean | null {
  const difference = subtractExact(a, b);
  if (difference === null) return null;
  return difference.startsWith("-");
}
